import { createTheme, MantineColorsTuple, MantineTheme } from '@mantine/core';

// Define custom colors that map to our CSS variables
const primaryBlue: MantineColorsTuple = [
  '#eff6ff', // 50
  '#dbeafe', // 100
  '#bfdbfe', // 200
  '#93c5fd', // 300
  '#60a5fa', // 400
  '#3b82f6', // 500 - our primary
  '#2563eb', // 600 - our primary-hover
  '#1d4ed8', // 700
  '#1e40af', // 800
  '#1e3a8a', // 900
];

export const mantineTheme = createTheme({
  colors: {
    primary: primaryBlue,
  },
  primaryColor: 'primary',
  
  // Use CSS variables for colors that need to respect theme switching
  components: {
    
    Button: {
      styles: (_theme: MantineTheme, params: any) => ({
        root: {
          // Only apply custom styles for specific variants
          ...(params.variant === 'filled' && {
            backgroundColor: 'var(--app-primary)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'var(--app-primary-hover)',
            },
          }),
          ...(params.variant === 'outline' && {
            borderColor: 'var(--app-border)',
            color: 'var(--app-text-primary)',
            '&:hover': {
              backgroundColor: 'var(--app-hover)',
            },
          }),
          ...(params.variant === 'default' && {
            backgroundColor: 'var(--app-surface)',
            borderColor: 'var(--app-border)',
            color: 'var(--app-text-primary)',
            '&:hover': {
              backgroundColor: 'var(--app-bg)',
              borderColor: 'var(--app-text-muted)',
            },
          }),
        },
      }),
    },

    Paper: {
      styles: () => ({
        root: {
          backgroundColor: 'var(--app-surface)',
          borderColor: 'var(--app-border)',
          color: 'var(--app-text-primary)',
        },
      }),
    },

    Input: {
      styles: () => ({
        input: {
          backgroundColor: 'var(--app-surface)',
          borderColor: 'var(--app-border)',
          color: 'var(--app-text-primary)',
          '&::placeholder': {
            color: 'var(--app-text-muted)',
          },
          '&:focus': {
            borderColor: 'var(--app-primary)',
          },
        },
        label: {
          color: 'var(--app-text-primary)',
        },
      }),
    },

    TextInput: {
      styles: () => ({
        label: {
          color: 'var(--app-text-primary)',
        },
      }),
    },

    Textarea: {
      styles: () => ({
        label: {
          color: 'var(--app-text-primary)',
        },
      }),
    },

    Popover: {
      styles: () => ({
        dropdown: {
          backgroundColor: 'var(--app-surface)',
          borderColor: 'var(--app-border)',
          color: 'var(--app-text-primary)',
        },
      }),
    },

    Select: {
      styles: () => ({
        label: {
          color: 'var(--app-text-primary)',
        },
        dropdown: {
          backgroundColor: 'var(--app-surface)',
          borderColor: 'var(--app-border)',
        },
        option: {
          color: 'var(--app-text-primary)',
          '&[data-selected]': {
            backgroundColor: 'var(--app-primary-light)',
            color: 'var(--app-text-primary)',
          },
          '&[data-hovered]': {
            backgroundColor: 'var(--app-hover)',
            color: 'var(--app-text-primary)',
          },
        },
      }),
    },

    Combobox: {
      styles: () => ({
        dropdown: {
          backgroundColor: 'var(--app-surface)',
          borderColor: 'var(--app-border)',
        },
        option: {
          color: 'var(--app-text-primary)',
          '&[data-selected]': {
            backgroundColor: 'var(--app-primary-light)',
            color: 'var(--app-text-primary)',
          },
          '&[data-hovered]': {
            backgroundColor: 'var(--app-hover)',
            color: 'var(--app-text-primary)',
          },
        },
      }),
    },

    Menu: {
      styles: () => ({
        dropdown: {
          backgroundColor: 'var(--app-surface)',
          borderColor: 'var(--app-border)',
        },
        item: {
          color: 'var(--app-text-primary)',
          '&[data-hovered]': {
            backgroundColor: 'var(--app-hover)',
            color: 'var(--app-text-primary)',
          },
        },
      }),
    },
  },

  // Font settings to match existing typography
  fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
  fontSizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
  },

  // Border radius to match existing design
  radius: {
    xs: '4px',
    sm: '6px',
    md: '8px',
    lg: '12px',
    xl: '16px',
  },

  // Spacing to match existing design
  spacing: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
});